import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const AchievementsList = ({ achievements, recentAchievements, totalPracticeTime }) => {
  const { t, formatTime } = useLanguage();

  const timeAchievements = [
    {
      id: 'firstStart',
      title: t('achievements.musicJourney'),
      description: t('achievements.descriptions.musicJourney'),
      emoji: '🎵',
      target: null,
      isSpecial: true
    },
    {
      id: 'fiveMinutes',
      title: t('achievements.rhythmSprite'),
      description: t('achievements.descriptions.rhythmSprite'),
      emoji: '⏱️',
      target: 300
    },
    {
      id: 'tenMinutes',
      title: t('achievements.beatExplorer'),
      description: t('achievements.descriptions.beatExplorer'),
      emoji: '⌛',
      target: 600
    },
    {
      id: 'twentyMinutes',
      title: t('achievements.rhythmGuardian'),
      description: t('achievements.descriptions.rhythmGuardian'),
      emoji: '🕐',
      target: 1200
    },
    {
      id: 'thirtyMinutes',
      title: t('achievements.beatWizard'),
      description: t('achievements.descriptions.beatWizard'),
      emoji: '🕑',
      target: 1800
    },
    {
      id: 'sixtyMinutes',
      title: t('achievements.rhythmMaster'),
      description: t('achievements.descriptions.rhythmMaster'),
      emoji: '🏆',
      target: 3600
    },
    {
      id: 'hundredTwentyMinutes',
      title: t('achievements.musicLegend'),
      description: t('achievements.descriptions.musicLegend'),
      emoji: '👑',
      target: 7200
    }
  ];

  const featureAchievements = [
    {
      id: 'changedTempo',
      title: t('achievements.speedAdventurer'),
      description: t('achievements.descriptions.speedAdventurer'),
      emoji: '🚀'
    },
    {
      id: 'changedTimeSignature',
      title: t('achievements.timeSigMagician'),
      description: t('achievements.descriptions.timeSigMagician'),
      emoji: '🔄'
    },
    {
      id: 'changedCharacter',
      title: t('achievements.creativeDesigner'),
      description: t('achievements.descriptions.creativeDesigner'),
      emoji: '🎭'
    },
    {
      id: 'tempoMaster',
      title: t('achievements.speedCollector'),
      description: t('achievements.descriptions.speedCollector'),
      emoji: '🎼'
    },
    {
      id: 'accessoriesMaster',
      title: t('achievements.fashionDesigner'),
      description: t('achievements.descriptions.fashionDesigner'),
      emoji: '✨'
    },
    {
      id: 'speedDemon',
      title: t('achievements.speedDemon'),
      description: t('achievements.descriptions.speedDemon'),
      emoji: '⚡'
    },
    {
      id: 'slowAndSteady',
      title: t('achievements.slowAndSteady'),
      description: t('achievements.descriptions.slowAndSteady'),
      emoji: '🐌'
    },
    {
      id: 'complexRhythm',
      title: t('achievements.complexRhythm'),
      description: t('achievements.descriptions.complexRhythm'),
      emoji: '🎯'
    }
  ];

  const getProgressPercentage = (currentValue, targetValue) => {
    return Math.min(100, (currentValue / targetValue) * 100);
  };

  const AchievementItem = ({ achievement, isUnlocked, progress = 100 }) => (
    <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 flex items-center space-x-4">
      <div
        className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
          isUnlocked
            ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
            : 'bg-gray-300 dark:bg-gray-600'
        }`}
      >
        <span className="text-lg">{achievement.emoji}</span>
      </div>
      <div className="flex-grow min-w-0">
        <h4 className="font-semibold text-base mb-1 text-gray-800 dark:text-gray-200">
          {achievement.title}
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          {achievement.description}
        </p>

        {achievement.target && !achievement.isSpecial && (
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>{formatTime(totalPracticeTime)}</span>
              <span>{formatTime(achievement.target)}</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {achievement.isSpecial && isUnlocked && (
          <div className="flex items-center space-x-2 mt-2">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
              ✨ {t('startingAchievement')}
            </span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Time achievements */}
      <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-xl">
        <h3 className="text-xl font-bold mb-6 text-purple-600 dark:text-purple-400">
          {t('practiceTimeAchievements')}
        </h3>
        <div className="space-y-4">
          {timeAchievements.map(achievement => (
            <AchievementItem
              key={achievement.id}
              achievement={achievement}
              isUnlocked={achievements[achievement.id]}
              progress={achievement.target ? getProgressPercentage(totalPracticeTime, achievement.target) : 100}
            />
          ))}
        </div>
      </div>

      {/* Feature achievements */}
      <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-xl">
        <h3 className="text-xl font-bold mb-6 text-purple-600 dark:text-purple-400">
          {t('explorationAchievements')}
        </h3>
        <div className="space-y-4">
          {featureAchievements.map(achievement => (
            <AchievementItem
              key={achievement.id}
              achievement={achievement}
              isUnlocked={achievements[achievement.id]}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AchievementsList;